import {assert} from '@pexip/utils';

import type {
    FacingMode,
    IndexedDevices,
    InputConstraintSet,
    InputDeviceConstraint,
    InputDevicePermission,
    MediaDeviceInfoLike,
    MediaDeviceRequest,
    MediaDeviceTrackInfo,
    MediaInput,
} from './types';
import {MediaDeviceKinds} from './types';
import {
    extractConstrainNumber,
    extractConstrainString,
    extractConstraintsWithKeys,
    normalizeDeviceConstraint,
    satisfyConstrainNumber,
    toArray,
} from './constraints';
import {
    isConstrainDOMStringParameters,
    isConstraintDOMString,
    isConstraintSetDevice,
    isInputConstraintSet,
    isMediaDeviceInfo,
    isMediaDeviceTrackInfo,
    isMediaStreamTrack,
} from './typeGuards';
import {
    compareDevices,
    findDevice,
    isAudioInput,
    isAudioOutput,
    isDeviceGranted,
    isSameDeviceKind,
    isVideoInput,
    not,
} from './utils';
import {DEVICE_ID_SEPARATOR} from './constants';

/**
 * DeviceChange Event
 *
 * ```
 * devices (raw)
 * ```
 *
 * @beta
 */
export type DeviceChangedChanges = {
    found: MediaDeviceInfoLike[];
    lost: MediaDeviceInfoLike[];
    devices: MediaDeviceInfoLike[];
};

interface ToKeyOptions {
    id: string;
    kind: string;
    label: string;
}

/**
 * Convert provided info to key for Map
 *
 * @beta
 */
export const toKey = ({id, kind, label}: ToKeyOptions): string =>
    [kind, id, label].join(DEVICE_ID_SEPARATOR);

export const toMediaDeviceInputKind = (
    track: Pick<MediaStreamTrack, 'kind'>,
): 'audioinput' | 'videoinput' => {
    switch (track.kind) {
        case 'audio':
            return 'audioinput';
        case 'video':
            return 'videoinput';
        default:
            throw new Error('Invalid track kind');
    }
};

/**
 * Future proofing: in case we want to alter the values or type returned
 * by {@link https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/enumerateDevices | enumerateDevices}
 *
 * @returns
 * a list of currently available {@link https://developer.mozilla.org/en-US/docs/Web/API/MediaDeviceInfo | devices}
 *
 * @example
 * ```javascript
 * import {getDevices} from "media-control";
 *
 * const devices = await getDevices();
 * // return MediaDeviceInfo[]
 * ```
 *
 * @beta
 */
export const getDevices = async () => {
    if (
        'mediaDevices' in navigator &&
        'enumerateDevices' in navigator.mediaDevices
    ) {
        const devices = await navigator.mediaDevices.enumerateDevices();

        return devices;
    }
    // Return empty list as there is no enumerateDevices
    return [];
};

export const toDeviceKey = (device: MediaDeviceInfoLike) =>
    toKey({id: device.deviceId, kind: device.kind, label: device.label});

export const toDeviceTuple = (device: MediaDeviceInfoLike) =>
    [toDeviceKey(device), device] as const;

export const toDevicesMap = (devices: MediaDeviceInfoLike[]) => {
    const map = new Map<string, MediaDeviceInfoLike>();
    for (const device of devices) {
        const key = toDeviceKey(device);
        if (!map.has(key)) {
            map.set(key, device);
        }
    }
    return map;
};

export const createTrackDevicesChanges = (
    prevDevices: MediaDeviceInfoLike[] = [],
) => {
    let seen = toDevicesMap(prevDevices);
    return (devices: MediaDeviceInfoLike[]) => {
        const found = [];
        // Remove duplicated
        const nextDeviceMap = toDevicesMap(devices);

        for (const [key, device] of nextDeviceMap) {
            if (!seen.delete(key)) {
                found.push(device);
            }
        }
        const lost = Array.from(seen.values());

        seen = nextDeviceMap;

        return {found, lost, devices: Array.from(nextDeviceMap.values())};
    };
};

/**
 * Unified interface for subscribing {@link DeviceChangedChanges} Event
 *
 * @beta
 */
export const deviceChanged = (fn: (event: DeviceChangedChanges) => void) => {
    const trackChanges = createTrackDevicesChanges();

    const odc = async () => {
        const devices = await getDevices();
        const changes = trackChanges(devices);
        fn(changes);
    };

    // Add extra checking to avoid calling unavailable feature
    // There is `ondevicechange` property in `navigator.mediaDevices` in Safari
    // 11 & 12 but it does nothing and there is no event listener functions
    // available since Safari does not support this API
    //
    // Ref. https://caniuse.com/#search=ondevicechange
    if (navigator.mediaDevices && 'ondevicechange' in navigator.mediaDevices) {
        navigator.mediaDevices.addEventListener('devicechange', odc);
        return () => {
            navigator.mediaDevices.removeEventListener('devicechange', odc);
        };
    }
    const it = window.setInterval(() => void odc(), 5000);
    return () => {
        window.clearInterval(it);
    };
};

export const extractDeviceInfo = (
    track: MediaStreamTrack,
): MediaDeviceTrackInfo => {
    const {deviceId, groupId, ...settings} = track.getSettings();
    const kind =
        track.kind === 'audio'
            ? MediaDeviceKinds.AUDIOINPUT
            : MediaDeviceKinds.VIDEOINPUT;
    return {
        id: track.id,
        kind,
        deviceId: deviceId ?? '',
        groupId: groupId ?? '',
        label: track.label,
        settings,
    };
};

/**
 * Convert `MediaStreamTrack` to `MediaDeviceInfoLike`
 *
 * @beta
 */
export const toMediaDeviceInfoLike = (
    track: MediaStreamTrack,
): MediaDeviceInfoLike | undefined => {
    const {deviceId, label, kind, groupId} = extractDeviceInfo(track);

    if (!deviceId) {
        return;
    }
    return {deviceId, label, kind, groupId};
};

/**
 * Compare the provide the MediaStreamTrack and MediaDeviceInfo to see if they
 * are the same with best efforts
 *
 * @param track - `MediaStreamTrack` used for the comparison
 * @param device - `MediaDeviceInfo` used for the comparison
 *
 * @returns `true` if they are the same else `false`
 *
 * @remarks
 * Old browser may not have `deviceId` from getSettings()
 * https://developer.mozilla.org/en-US/docs/Web/API/MediaTrackSettings
 * The `label` attribute will then be used for the comparison instead of the
 * `deviceId`
 *
 * @beta
 */
export const compareMediaDeviceToMediaTrack =
    (track: Pick<MediaStreamTrack, 'kind' | 'getSettings' | 'label'>) =>
    (device: MediaDeviceInfoLike) => {
        const {deviceId} = track.getSettings();

        const sameLabel = device.label === track.label;
        const sameKind = device.kind.startsWith(track.kind);
        // Old browser may not have deviceId from getSettings()
        // https://developer.mozilla.org/en-US/docs/Web/API/MediaTrackSettings
        if (deviceId) {
            return sameKind && deviceId === device.deviceId;
        }
        return sameKind && sameLabel;
    };

export const toMediaDeviceInfo = (info: MediaDeviceInfoLike) => ({
    deviceId: info.deviceId,
    groupId: info.groupId,
    kind: info.kind,
    label: info.label,
});

/**
 * Find the MediaDeviceInfo from provided MediaDeviceInfo[] by comparing with
 * provided MediaStreamTrack
 *
 * @param devices - A device list used for the searching
 * @param track - A track for the searching criteria
 *
 * @returns `MediaDeviceInfo` if found, otherwise `undefined`
 *
 * @beta
 */
export const findMediaInputFromMediaStreamTrack =
    (devices: IndexedDevices) =>
    (track?: MediaStreamTrack | undefined): MediaDeviceInfoLike | undefined => {
        if (!track) {
            return undefined;
        }
        const kind = toMediaDeviceInputKind(track);
        if (!devices.size(kind)) {
            return undefined;
        }
        const settings = track.getSettings();
        const devicesFound = devices.get({
            kind,
            label: track.label,
            deviceId: settings.deviceId,
        });
        return devicesFound[0];
    };

/**
 * Find media input from media stream
 *
 * @param devices - A list of media devices from
 * `navigator.mediaDevices.enumerateDevices()`. Will be used for search target
 * @param stream - A media stream used for the search criteria
 *
 * @returns A object may contains the devices
 *
 * @beta
 */
export const findMediaInputFromStream =
    (devices: IndexedDevices) =>
    (stream: MediaStream | undefined): MediaInput => {
        if (!stream) {
            return {};
        }
        const findMediaInput = findMediaInputFromMediaStreamTrack(devices);
        return {
            audioInput: findMediaInput(...stream.getAudioTracks()),
            videoInput: findMediaInput(...stream.getVideoTracks()),
        };
    };

export const compareDeviceConstraintAndTrackDevice =
    (device: MediaDeviceInfoLike | undefined) =>
    (constraint: InputConstraintSet['device']) => {
        if (!device || !constraint) {
            return Boolean(device) === Boolean(constraint);
        }
        const normalized = normalizeDeviceConstraint(constraint);
        if (normalized) {
            const requestingDevices = Object.values(
                normalized as Record<string, MediaDeviceInfoLike[]>,
            ).flatMap(t => (Array.isArray(t) ? t : [t]));
            const compare = compareDevices(
                device,
                // Always use `deviceId` for exact constraints
                !!normalized.exact || !device.label ? 'deviceId' : 'label',
            );
            return requestingDevices.some(compare);
        }
        return false;
    };

export const isRequestedResolution = (
    request: InputDeviceConstraint | undefined,
    response: MediaTrackSettings | undefined,
) => {
    if (!response || !request) {
        return Boolean(response) === Boolean(request);
    }
    if (isInputConstraintSet(request)) {
        const [width] = extractConstrainNumber('width')(request);
        const [height] = extractConstrainNumber('height')(request);
        const {width: responseWidth = 0, height: responseHeight = 0} =
            response ?? {};
        const portrait = responseHeight >= responseWidth;
        const resultWidth = portrait ? responseHeight : responseWidth;
        const resultHeight = portrait ? responseWidth : responseHeight;
        const sameWidth = satisfyConstrainNumber(width, resultWidth);
        const sameHeight = satisfyConstrainNumber(height, resultHeight);
        return sameWidth && sameHeight;
    }
    return true;
};

export const isRequestedInputDevice = (
    request: InputDeviceConstraint | undefined,
    response: MediaDeviceInfoLike | MediaDeviceTrackInfo | undefined,
) => {
    if (!response || !request) {
        return Boolean(response) === Boolean(request);
    }
    const compareDevice = compareDeviceConstraintAndTrackDevice(response);
    if (isInputConstraintSet(request)) {
        if (isConstraintSetDevice(request.device)) {
            const isSameDevice = compareDevice(request.device);
            return isSameDevice;
        }
        if (request.deviceId) {
            const {deviceId} = response;
            if (!deviceId) {
                return true;
            }
            if (isConstraintDOMString(request.deviceId)) {
                const ids = toArray(request.deviceId);
                return ids.some(id => deviceId === id);
            }
            if (isConstrainDOMStringParameters(request.deviceId)) {
                return Object.values(request.deviceId)
                    .flatMap(toArray)
                    .some(id => id === deviceId);
            }
        }
        if (isMediaDeviceTrackInfo(response)) {
            const facingMode = response.settings.facingMode;
            if (request.facingMode && facingMode) {
                const [facingModes] =
                    extractConstrainString('facingMode')(request);
                return facingModes?.some(fm => fm === facingMode) ?? true;
            }
        }
        return true;
    }
    if (isConstraintSetDevice(request)) {
        return compareDevice(request);
    }
    return !!response;
};

/**
 * Compare request and the input to see if the request has been fulfilled
 */
export const isRequestedInputTrack = (
    request: InputDeviceConstraint | undefined,
    current: MediaStreamTrack | MediaDeviceInfoLike | undefined,
): boolean => {
    if (!request || !current) {
        return Boolean(request) === Boolean(current);
    }

    if (isMediaDeviceInfo(current)) {
        return isRequestedInputDevice(request, current);
    }
    if (isMediaStreamTrack(current)) {
        const device = extractDeviceInfo(current);
        return isRequestedInputDevice(request, device);
    }
    return true;
};

const extractDevice = extractConstraintsWithKeys(['device', 'deviceId']);
/**
 * Check if there is a requesting device with respecting to the current devices
 *
 * @param request - Requesting input device constraint
 * @param kind - The kind of the device
 * @param currentDevices - The current indexed devices
 */
export const hasRequestingDevice = (
    request: InputDeviceConstraint | undefined,
    kind: MediaDeviceKind,
    currentDevices: IndexedDevices,
): boolean => {
    if (!request) {
        return false;
    }
    const {
        device: [devices],
        deviceId: [deviceIds],
    } = extractDevice(request);
    // No device specified
    if (
        (!devices || devices.length === 0) &&
        (!deviceIds || deviceIds.length === 0)
    ) {
        return Boolean(currentDevices.size(kind));
    }
    // Has device specified
    return (
        devices?.some(device => currentDevices.get(device).length) ??
        deviceIds?.some(
            deviceId => currentDevices.get({kind, deviceId}).length,
        ) ??
        false
    );
};

/**
 * Check if the track is muted or 'ended'
 * @param track - The media stream tracks
 */
export const isTrackMutedOrEnded = (track: MediaStreamTrack) =>
    track.readyState === 'ended' || track.muted;
interface DeviceRequestParams {
    kind: 'audioinput' | 'videoinput';
    permission: PermissionState;
    request: InputDeviceConstraint | undefined;
    tracks: MediaStreamTrack[];
    currentDevices: IndexedDevices;
}
/**
 * Decide if we should send a new gUM request
 *
 * @param deviceRequestParams - @see DeviceRequestParams
 *
 * @returns `true` means the request should be conducted, otherwise `false`.
 */
export const shouldRequestDevice = ({
    kind,
    permission,
    request,
    tracks,
    currentDevices,
}: DeviceRequestParams): boolean => {
    if (!request) {
        return false;
    }
    switch (permission) {
        case 'prompt':
        case 'granted': {
            if (currentDevices.size(kind) === 0) {
                return false;
            }
            if (tracks.length === 0 || tracks.some(isTrackMutedOrEnded)) {
                return true;
            }
            const [track] = tracks;
            assert(track);
            if (isRequestedInputTrack(request, track)) {
                return false;
            }
            if (hasRequestingDevice(request, kind, currentDevices)) {
                return true;
            }
            return false;
        }
        default:
            return false;
    }
};

export const resolveInputDevice = (
    tracksOrDevice: MediaDeviceInfoLike | MediaStreamTrack[] | undefined,
    findInput: ReturnType<typeof findMediaInputFromMediaStreamTrack>,
) => {
    if (isMediaDeviceInfo(tracksOrDevice)) {
        return tracksOrDevice;
    }
    if (Array.isArray(tracksOrDevice)) {
        const [track] = tracksOrDevice;
        if (track?.readyState === 'live') {
            return findInput(track) ?? track;
        }
    }
    return undefined;
};

export const isStreamingRequestingDevice = (
    request: InputDeviceConstraint | undefined,
    trackOrDevice: MediaDeviceInfoLike | MediaStreamTrack[] | undefined,
    devices: IndexedDevices,
) => {
    if (request === undefined) {
        return true;
    }
    const findInput = findMediaInputFromMediaStreamTrack(devices);
    const input = resolveInputDevice(trackOrDevice, findInput);
    if (!input) {
        return Boolean(request) === Boolean(input);
    }
    return isRequestedInputTrack(request, input);
};

export const isStreamingRequestedDevicesBase = (
    request: MediaDeviceRequest,
    tracksOrDevices: {
        audio?: MediaDeviceInfoLike | MediaStreamTrack[];
        video?: MediaDeviceInfoLike | MediaStreamTrack[];
    },
    devices: IndexedDevices,
) => {
    const audio = isStreamingRequestingDevice(
        request.audio,
        tracksOrDevices.audio,
        devices,
    );
    const video = isStreamingRequestingDevice(
        request.video,
        tracksOrDevices.video,
        devices,
    );
    return {audio, video};
};

/**
 * Check if provided request has already been fulfilled
 *
 * @param request - A media request constraints
 * @param stream - Current media stream
 *
 * @returns
 * audio - The stream is using the same device as requested if `true`
 * video - The stream is using the same device as requested if `true`
 */
export const isStreamingRequestedDevices = (
    request: MediaDeviceRequest,
    stream: MediaStream | undefined,
    devices: IndexedDevices,
) =>
    isStreamingRequestedDevicesBase(
        request,
        {
            audio: stream?.getAudioTracks(),
            video: stream?.getVideoTracks(),
        },
        devices,
    );

/**
 * Find in the list of devices which has permissions granted
 *
 * From MDN:
 * For security reasons,the label field is always blank unless an active media stream
 * exists or the user has granted persistent permission for media device access.
 *
 * @param devices - A list of media devices from
 * `navigator.mediaDevices.enumerateDevices()`. Will be used for search target
 *
 * @returns List of devices that has permission granted
 *
 * @beta
 */
export const findPermissionGrantedDevices = (devices: MediaDeviceInfoLike[]) =>
    devices.filter(isDeviceGranted);

/**
 * Use provided devices to guess the permission state. When there is no active
 * stream and there is no device with label, 'prompt' will be returned
 * otherwise, 'denied'.
 *
 * @param anyGrantedDevice - Any permission granted device
 * @param anyLiveTrack - Has ever got a live track
 * guess the state more accurately
 */
export const toPermissionState = (
    anyGrantedDevice: boolean,
    anyLiveTrack = false,
): PermissionState => {
    if (anyLiveTrack) {
        return anyGrantedDevice ? 'granted' : 'denied';
    }
    return anyGrantedDevice ? 'granted' : 'prompt';
};

export const queryPermissionFallback = async (
    name: 'camera' | 'microphone',
    everActiveTrack = false,
    getDevices = () => navigator.mediaDevices.enumerateDevices(),
) => {
    const devices = await getDevices();
    const kind = name === 'camera' ? 'videoinput' : 'audioinput';

    return toPermissionState(
        devices.some(device => device.kind === kind && isDeviceGranted(device)),
        everActiveTrack,
    );
};

export const queryPermission = async (
    name: 'camera' | 'microphone',
    everActiveTrack = false,
    getDevices = () => navigator.mediaDevices.enumerateDevices(),
) => {
    try {
        // @ts-expect-error -- Typings/dom.d.ts change blocks upgrade to typescript 4.4
        const {state} = await navigator.permissions.query({name});
        // Firefox v133 added permission API but always returns granted but
        // based on the device label, it is actually not granted, so we need to
        // fallback to use `enumerateDevices` to guess the state
        if (state === 'granted') {
            return await queryPermissionFallback(
                name,
                everActiveTrack,
                getDevices,
            );
        }
        return state;
    } catch {
        return await queryPermissionFallback(name, everActiveTrack, getDevices);
    }
};

/**
 * A wrapper for `navigator.permissions.query` with fallback to use
 * `navigator.mediaDevices.enumerateDevices` to guess the `PermissionState`
 *
 * @param anyActiveAudioTrack - Has ever got an active audio to help the fallback to
 * guess the state more accurately
 * @param anyActiveVideoTrack - Has ever got an active video to help the fallback to
 * guess the state more accurately
 * @param getDevices - A function to get the list of devices, e.g. navigator.mediaDevices.enumerateDevices
 */
export const getInputDevicePermissionState = async (
    anyActiveAudioTrack = false,
    anyActiveVideoTrack = false,
    getDevices = () => navigator.mediaDevices.enumerateDevices(),
): Promise<InputDevicePermission> => {
    const [audio, video] = await Promise.all([
        queryPermission('microphone', anyActiveAudioTrack, getDevices),
        queryPermission('camera', anyActiveVideoTrack, getDevices),
    ]);
    return {audio, video};
};

/**
 * Find current audio output id to be used to set as sinkId
 *
 * If you set the stale deviceId to setSink it will throw exception.
 * So we want to check if audio output id still exist.
 *
 * @param audioOutput - Audio output as `MediaDeviceInfo`
 * @param devices - A list of media devices from
 * `navigator.mediaDevices.enumerateDevices()`. Will be used for search target
 *
 * @returns Audio output id or empty string if couldn't find it.
 *
 * @beta
 */
export const findCurrentAudioOutputId = (
    audioOutput?: MediaDeviceInfoLike,
    devices?: MediaDeviceInfoLike[],
) => {
    let selectedAudioOutputDeviceId = '';

    if (audioOutput?.deviceId && devices) {
        const latestSelectedAudioInput = findDevice(audioOutput)(devices);

        if (latestSelectedAudioInput) {
            selectedAudioOutputDeviceId = latestSelectedAudioInput.deviceId;
        }
    }

    return selectedAudioOutputDeviceId;
};

/**
 * Find videoinput device id in the stream
 *
 * @param stream - Media stream to do the lookup
 *
 * @returns A object may contains the devices
 *
 * @beta
 */
export const findCurrentVideoInputDeviceIdFromStream = (
    stream: MediaStream,
) => {
    const [videoTrack] = stream.getVideoTracks();
    return videoTrack && toMediaDeviceInfoLike(videoTrack)?.deviceId;
};

/**
 * Finds device with given deviceId
 *
 * @param devices - A list of media devices from
 * `navigator.mediaDevices.enumerateDevices()`. Will be used for search target
 * @param deviceId - id that represents desired device
 *
 * @returns device with given deviceId
 *
 * @beta
 */
export const findDeviceWithDeviceId = (
    devices: MediaDeviceInfoLike[],
    deviceId: string,
) => devices.find(d => d.deviceId === deviceId);

/**
 * Find in the list of devices with given MediaDeviceKind
 *
 * @param devices - A list of media devices from
 * `navigator.mediaDevices.enumerateDevices()`. Will be used for search target
 * @param kind - A list of media devices from
 * `navigator.mediaDevices.enumerateDevices()`. Will be used for search target
 *
 * @returns List of devices that have required MediaDeviceKind
 *
 * @beta
 */
export const findDevicesByKind =
    (kind: MediaDeviceKind) => (devices: MediaDeviceInfoLike[]) =>
        devices.filter(isSameDeviceKind(kind as MediaDeviceKinds));

/**
 * Find in the list of devices only the audio input ones
 *
 * @param devices - A list of media devices from
 * `navigator.mediaDevices.enumerateDevices()`. Will be used for search target
 *
 * @returns List of devices that are audio inputs
 *
 * @beta
 */
export const findAudioInputDevices = findDevicesByKind(
    MediaDeviceKinds.AUDIOINPUT,
);

/**
 * Find in the list of devices only the video input ones
 *
 * @param devices - A list of media devices from
 * `navigator.mediaDevices.enumerateDevices()`. Will be used for search target
 *
 * @returns List of devices that are video inputs
 *
 * @beta
 */
export const findVideoInputDevices = findDevicesByKind(
    MediaDeviceKinds.VIDEOINPUT,
);

/**
 * Find in the list of devices only the audio output ones
 *
 * @param devices - A list of media devices from
 * `navigator.mediaDevices.enumerateDevices()`. Will be used for search target
 *
 * @returns List of devices that are audio outputs
 *
 * @beta
 */
export const findAudioOutputDevices = findDevicesByKind(
    MediaDeviceKinds.AUDIOOUTPUT,
);

const muteTrack = (tracks: MediaStreamTrack[], muted: boolean) => {
    for (const track of tracks) {
        track.enabled = !muted;
    }
};

/**
 * Set `MediaStreamTrack['enabled']` according to `mute` param for the provided `stream`
 *
 * @param stream - Media stream
 * @param mute - disable or enable the audio stream
 * @param mediaType - Can either be 'audio', 'video' or 'all'
 * @defaultValue
 * 'all'
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/MediaStreamTrack/enabled
 *
 * @beta
 */
export const muteStreamTrack =
    (stream?: MediaStream) =>
    (mute: boolean, mediaType: 'audio' | 'video' | 'all' = 'all') => {
        if (!stream) {
            return;
        }
        switch (mediaType) {
            case 'audio':
                muteTrack(stream?.getAudioTracks(), mute);
                break;
            case 'video':
                muteTrack(stream.getVideoTracks(), mute);
                break;
            default:
                muteTrack(stream.getTracks(), mute);
                break;
        }
    };

/**
 * Stops all tracks in the given stream
 *
 * Immediately after calling stop(), the readyState property is set to `ended`.
 * Note that the `ended` event will not be fired in this situation
 * https://developer.mozilla.org/en-US/docs/Web/API/MediaStreamTrack/stop#description
 *
 * @param stream - `MediaStream` which we mutate
 * @param onStopped - callback to be called when the track is stopped
 */
export const stopMediaStream = (
    stream: MediaStream | undefined,
    onStopped?: (track: MediaStreamTrack) => void,
) => {
    if (!stream) {
        return;
    }
    for (const track of stream.getTracks()) {
        track.stop();
        onStopped?.(track);
    }
};

/**
 * Checks that tracks with a given type are enabled in the stream
 *
 * @param stream - `MediaStream` used for comparison
 * @param type - `MediaInput` used for comparison
 *
 * @returns Return true when all the tracks are enabled false otherwise
 *
 * @beta
 */
export const areTracksEnabled = (
    stream: MediaStream | undefined,
    type: 'audio' | 'video',
) => {
    if (!stream) {
        return false;
    }
    const tracks: MediaStreamTrack[] =
        type === 'audio' ? stream.getAudioTracks() : stream.getVideoTracks();
    return tracks.length > 0 && tracks.every(track => track.enabled);
};

/**
 * Check if list contains media inputs
 *
 * @param devices - A list of media devices from
 * `navigator.mediaDevices.enumerateDevices()`. Will be used for search target
 *
 * @returns True when some media inputs false otherwise
 *
 * @beta
 */

export const hasAudioOrVideoInputs = (devices: MediaDeviceInfoLike[]) =>
    devices.some(not(isAudioOutput));

/**
 * Check if list contains audio inputs
 *
 * @param devices - A list of media devices from
 * `navigator.mediaDevices.enumerateDevices()`. Will be used for search target
 *
 * @returns True when some audio inputs false otherwise
 *
 * @beta
 */

export const hasAudioInputs = (devices: MediaDeviceInfoLike[]) =>
    devices.some(isAudioInput);

/**
 * Check if list contains video inputs
 *
 * @param devices - A list of media devices from
 * `navigator.mediaDevices.enumerateDevices()`. Will be used for search target
 *
 * @returns True when some video inputs false otherwise
 *
 * @beta
 */

export const hasVideoInputs = (devices: MediaDeviceInfoLike[]) =>
    devices.some(isVideoInput);

/**
 * Check if provided devices have any granted input device
 *
 * @param devices - The devices to check
 */
export const hasAnyGrantedInput = (devices: MediaDeviceInfoLike[]) =>
    devices.some(
        device =>
            isDeviceGranted(device) &&
            (isVideoInput(device) || isAudioInput(device)),
    );

/**
 * Check if provided devices have any video and audio inputs
 *
 * @param devices - The devices to lookup
 * @param grantedOnly - When it is `true`, the device label is also taken into
 * consideration
 * @defaultValue `false`
 *
 * @returns A tuple of `[anyAudioInput, anyVideoInput]`, e.g. `[true, false]`
 * means there is audio input but no video input
 */
export const hasAnyInputs = (
    devices: MediaDeviceInfoLike[],
    grantedOnly = false,
): [boolean, boolean] => {
    let anyAudioDevices = false;
    let anyVideoDevices = false;
    for (const device of devices) {
        if (
            isAudioOutput(device) ||
            (grantedOnly && !isDeviceGranted(device))
        ) {
            continue;
        }
        if (isAudioInput(device)) {
            anyAudioDevices = true;
        }
        if (isVideoInput(device)) {
            anyVideoDevices = true;
        }
        if (anyVideoDevices && anyAudioDevices) {
            return [true, true];
        }
    }
    return [anyAudioDevices, anyVideoDevices];
};

/**
 * An utility function to check the input device has been changed
 *
 * @param oldInput - The previous input device
 * @param newInput - The current input device
 */
export const hasChangedInput = (
    oldInput: MediaDeviceInfoLike | undefined,
    newInput: MediaDeviceInfoLike | undefined,
) => {
    if (isMediaDeviceInfo(newInput)) {
        if (
            (isMediaDeviceInfo(oldInput) &&
                !compareDevices(oldInput)(newInput)) ||
            !oldInput
        ) {
            return true;
        }
    } else if (oldInput) {
        return true;
    }
    return false;
};

/**
 * An utility function to check if facing mode is supported by interpreting the
 * device label and the API `getSupportedConstraints`
 *
 * @remarks
 * Only support snooping the label for English
 *
 * @param currentDevices - Current list of devices available
 * @param getSupportedConstraints - A function to get supported constraints,
 * i.e. `navigator.mediaDevices.getSupportedConstraints()`
 * @param tracks - Current video input track
 */
export const areMultipleFacingModeSupported = (
    currentDevices: MediaDeviceInfoLike[],
    getSupportedConstraints = () =>
        navigator.mediaDevices.getSupportedConstraints(),
): boolean => {
    if (!getSupportedConstraints().facingMode) {
        return false;
    }
    const set = new Set();
    // Device label for built-in cameras usually implies with facing capability,
    // e.g. "Front camera", "Back camera" and "camera2 1, facing front"
    const hasFrontAndBackCameras = currentDevices.some(device => {
        if (device.kind !== 'videoinput') {
            return false;
        }
        if (device.label.match(/front/i)) {
            set.add('front');
        }
        if (device.label.match(/(back|rear)/i)) {
            set.add('back');
        }
        return set.size > 1;
    });
    if (!hasFrontAndBackCameras) {
        return false;
    }
    return true;
};

/**
 * Interpret the current facing mode from the provided track, and try to get the
 * mode from settings, or use the label to guess the facing mode when facingMode
 * is not supported from settings
 *
 * @param currentTrack - The current video input track
 */
export const interpretCurrentFacingMode = (
    currentTrack: MediaStreamTrack | undefined,
): FacingMode | undefined => {
    if (!currentTrack || currentTrack.kind !== 'video') {
        return undefined;
    }
    const settings = currentTrack.getSettings();
    if ('getCapabilities' in currentTrack) {
        const capabilities = currentTrack.getCapabilities();
        if (capabilities.facingMode && capabilities.facingMode.length > 0) {
            return settings.facingMode as FacingMode | undefined;
        }
    }
    // Firefox does not support `getCapabilities`, and it always returns 'user' by default
    // https://developer.mozilla.org/en-US/docs/Web/API/MediaStreamTrack/getCapabilities
    if (settings.facingMode) {
        return settings.facingMode as FacingMode;
    }
    // Firefox Android does not support facingMode from settings, use the label
    // to interpret the facing mode
    // https://developer.mozilla.org/en-US/docs/Web/API/MediaTrackSettings/facingMode
    if (currentTrack.label.match(/front/i)) {
        return 'user';
    }
    if (currentTrack.label.match(/(back|rear)/i)) {
        return 'environment';
    }
};
